import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { InventoryCategoryModal } from '../components/InventoryCategoryModal';
import { inventoryCategoriesApi } from '../inventoryApi';
import type { InventoryCategoryListItem } from '../inventoryTypes';
import { ITEM_KIND_LABEL } from '../inventoryTypes';

/**
 * What an item IS — the classification every catalog item is filed under.
 *
 * Reached through **Logistics → Inventory → Settings**, never as a sidebar row of its own. That
 * is load-bearing rather than tidy: `inventory-items.read` implies this catalog's read
 * UNSCOPED, which is only correct while it has no surface for that grant to arrive at.
 */
export function InventoryCategoriesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<InventoryCategoryListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    category: InventoryCategoryListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchCategories = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      inventoryCategoriesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (category: InventoryCategoryListItem): MenuItem[] => [
      {
        key: `edit-${category.id}`,
        label: 'Edit',
        permission: 'inventory-categories.update',
        onClick: () => setEditing(category),
      },
      {
        key: `restore-${category.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'inventory-categories.delete',
        disabledReason: category.isActive ? 'This category is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', category }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await inventoryCategoriesApi.deactivate(confirming.category.id);
      else await inventoryCategoriesApi.activate(confirming.category.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<InventoryCategoryListItem>
        module="inventory-categories"
        breadcrumb={[
          'Logistics',
          'Inventory',
          { label: 'Settings', to: '/inventory/settings' },
          'Categories',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['InventoryCategory']}
        rowKey={(category) => category.id}
        fetch={fetchCategories}
        reloadKey={reloadKey}
        searchPlaceholder="Search categories"
        emptyMessage="No categories yet. Add the classifications items are filed under."
        onCreate={() => setCreating(true)}
        createPermission="inventory-categories.create"
        onRowOpen={(category) => navigate(`/inventory/settings/categories/${category.id}`)}
        openPermission="inventory-categories.read"
        rowActions={rowActions}
        onDelete={(category) => setConfirming({ kind: 'deactivate', category })}
        deletePermission="inventory-categories.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(category) =>
          category.isActive ? null : 'This category is already deleted.'
        }
        onClearData={() => inventoryCategoriesApi.clear()}
        clearDataPermission="inventory-categories.clear-data"
        otherInfo={(category) => category}
      />

      {creating || editing !== null ? (
        <InventoryCategoryModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete category' : 'Restore category'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`inventory-category-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.category.name}" stops being offered on the item form.`
                  : `"${confirming.category.name}" is offered again on the item form.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.category.itemCount > 0 ? (
                <p>
                  {confirming.category.itemCount} item
                  {confirming.category.itemCount === 1 ? '' : 's'}{' '}
                  {confirming.category.itemCount === 1 ? 'is' : 'are'} filed under it. They keep
                  resolving its name — deleting a word does not re-file what was recorded — but it
                  cannot be chosen for anything new.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted category can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<InventoryCategoryListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 260,
    flex: true,
    sortable: true,
    render: (category) => <TextCell value={category.name} />,
  },
  {
    key: 'code',
    header: 'Code',
    width: 130,
    sortable: true,
    summary: true,
    render: (category) => <TextCell value={category.code} mono />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 300,
    summary: true,
    render: (category) => <TextCell value={category.description} />,
  },
  {
    key: 'defaultKind',
    header: 'Usual kind',
    width: 150,
    summary: true,
    render: (category) => (
      <TextCell value={category.defaultKind === null ? null : ITEM_KIND_LABEL[category.defaultKind]} />
    ),
  },
  {
    key: 'itemCount',
    header: 'Items',
    width: 90,
    align: 'right',
    sortable: true,
    // NOT gated: `inventory-categories.read` does not imply the item module, but the count is
    // this catalog's own derived figure rather than another module's field — a reader of the
    // vocabulary is entitled to know whether a word is in use before retiring it.
    render: (category) => <TextCell value={String(category.itemCount)} mono numeric />,
  },
  {
    /*
      "Order", never "Level". A position can only be stated correctly with the whole ladder in
      hand, and this is a page of records — so the column shows the stored sort value under a
      heading that says so, and the pickers show the position.
    */
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (category) => <TextCell value={String(category.sortOrder)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (category) =>
      category.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
